import { GetServerSideProps } from 'next';
import React from 'react';

import { DefaultPage, RenderBlocks } from '@klappir/container/graphcms';
import { getPartner } from '@klappir/data/graphcms';
import { DataGraphcms, UIText } from '@klappir/types';
import { getColor } from '@klappir/ui/brand';
import { Hidden, SEO, Visible } from '@klappir/ui/core';
import { Share, StyledShare } from '@klappir/ui/social';
import { H3, Paragraph } from '@klappir/ui/text';
import { fonts, rem } from '@klappir/util/styles';
import styled from '@klappir/vendor/styled';

const PartnerHero = styled.div`
  position: relative;

  margin: 0 auto ${rem(120)};
  display: grid;

  grid-template-columns: 0 5% 44% auto;
  grid-column-gap: 36px;
  align-items: start;

  grid-template-areas: '. share summary image';

  @media only screen and (max-width: 860px) {
    margin: 0 auto 0;
    grid-template-columns: 0 0 100% auto;
    grid-column-gap: 18px;
  }

  ${StyledShare} {
    padding: ${rem(105)} 0 0;
    flex-direction: column;
    justify-content: center;

    ${Paragraph} {
      display: none;
    }
  }
`;

const ImageWrap = styled.div<{ image: DataGraphcms.Asset }>`
  display: block;
  background: transparent;

  background-image: url(${({ image }) => image.url});
  background-size: cover;
  background-position: center;

  grid-area: image;

  width: 100%;
  height: 50vh;
  min-height: 30vw;

  @media only screen and (max-width: 800px) {
    height: 30vh;
    width: 90%;
    margin: auto;
    margin-bottom: ${rem(16)};
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  align-items: center;
  padding: ${rem(105)} ${rem(72)} ${rem(16)} 0;

  grid-area: summary;

  ${H3} {
    margin: 0 0 ${rem(24)};
  }
  @media only screen and (max-width: 800px) {
    padding: ${rem(16)} ${rem(72)} 0 0;
  }
`;

const PartnerHeading = styled(H3).attrs({ as: 'h1' })`
  font-weight: ${fonts.weight.semibold};
`;

const Category = styled.span.attrs({ as: 'date' })<UIText.DateProps>`
  display: ${({ block }) => (block ? 'block' : 'inline-block')};

  font-family: ${fonts.primaryFont};
  font-weight: ${fonts.weight.regular};
  font-size: ${rem(14)};
  color: ${getColor('gray', 40)};
`;

const Partner = ({ content }: DataGraphcms.PartnerPageProps) => {
  const canonical = content?.slug
    ? `https://www.klappir.com/partners/${content.slug}`
    : undefined;

  return (
    <DefaultPage localizations={content.localizations}>
      <SEO
        title={content?.seo?.title ?? content?.title}
        description={content?.seo?.description ?? content?.summary}
        image={content?.seo?.image ?? content?.image}
        canonical={canonical}
      />

      <PartnerHero>
        <Hidden alwaysRender tiny mobile sm>
          <Share
            url={canonical}
            title={content?.title}
            description={content?.summary}
            direction={'column'}
            showLabel={false}
          />
        </Hidden>
        <ContentWrapper>
          <Category>Partners</Category>
          <PartnerHeading>{content?.title}</PartnerHeading>
          <Paragraph>{content?.summary}</Paragraph>
        </ContentWrapper>
        <Hidden alwaysRender tiny mobile sm>
          {content?.image?.url && <ImageWrap image={content?.image} />}
        </Hidden>
      </PartnerHero>
      <Visible alwaysRender tiny mobile sm>
        {content?.image?.url && <ImageWrap image={content?.image} />}
      </Visible>
      <RenderBlocks blocks={content?.blocks ?? []} />
    </DefaultPage>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const slug = (context?.params?.slug as string[]).join();
  const content = await getPartner(slug, context.locale);

  if (!content) {
    return {
      notFound: true,
    };
  }

  return {
    props: { content },
  };
};

export default Partner;
